"use client";

import {
  CSSProperties,
  FC,
  Fragment,
  ReactNode,
  useRef,
  useState,
} from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import cn from "classnames";
import { CaretDownLine } from "@/icons";
import { VerticalTabPaging } from "./VerticalTab";
import { VerticalTabPager } from "./VerticalTabPager/VerticalTabPager";
import { Button } from "@/components/Button";

export type VerticalTabMobileProps = {
  variant: "default" | "multiline";
  tabs: {
    label: string;
    head?: string;
    description?: string;
    dlt: string;
    dld: string;
  }[];
  children: ReactNode[];
  theme?: "black" | "gray" | "white";
  tabHeader?: string;
  previousAnalytics?: { dlt: string; dld: string };
  nextAnalytics?: { dlt: string; dld: string };
  nextItem?: {
    displayNextItemLink?: boolean;
    nextItemText: string;
    dlt: string;
    dld: string;
  };
  shownFeatures?: number;
  pagerLabel?: string;
  pagerContainerStyle?: CSSProperties;
  tabPaging: VerticalTabPaging;
  tabClickOverride?: boolean;
};

const VARIANT_MAPPING = {
  default: {
    borderColor: "border-swdc-silver-300!",
    tabButtonGap: "gap-4",
    label: "sm:text-subhead-xs text-subhead-2xs",
  },
  multiline: {
    borderColor: "border-swdc-orange-400!",
    tabButtonGap: "gap-6",
    label: "sm:text-link-m text-link-s font-bold",
  },
};

export const VerticalTabMobile: FC<VerticalTabMobileProps> = ({
  variant,
  theme,
  tabs,
  children,
  tabHeader,
  previousAnalytics,
  nextAnalytics,
  nextItem,
  shownFeatures = 4,
  pagerLabel,
  pagerContainerStyle,
  tabPaging,
}) => {
  const [selectedPanel, setSelectedPanel] = useState<number | null>(0);
  const tabsLength = tabs.length || 0;

  const tabRefs = useRef<
    { button: HTMLButtonElement | null; close: () => void }[]
  >([]);

  const toggleSelectedPanel = (index: number) => {
    if (selectedPanel !== index && selectedPanel !== null) {
      tabRefs.current[selectedPanel].close();
    }
    setSelectedPanel(index);
  };

  const handleNextTabClick = (currentIndex: number) => {
    //Scroll into view next tab if not visible
    const letLastTab = tabPaging?.lastTab ?? 0;
    if (currentIndex + 1 >= letLastTab) {
      handleArrowsClick(true);
    }
    tabRefs.current[currentIndex + 1]?.button?.click();
  };

  const showPagination = tabsLength > shownFeatures;
  const isFirstDisplayed = tabPaging?.firstTab === 0;
  const isLastDisplayed = tabPaging?.lastTab === tabsLength - 1;

  const handleArrowsClick = (showNext: boolean) => {
    if ((showNext && isLastDisplayed) || (!showNext && isFirstDisplayed))
      return;

    const shouldClosePanel = showNext
      ? tabPaging?.firstTab === selectedPanel
      : tabPaging?.lastTab === selectedPanel;
    if (shouldClosePanel && selectedPanel !== null) {
      tabRefs.current[selectedPanel].close();
      setSelectedPanel(null);
    }

    tabPaging?.setFirstTab(
      showNext ? tabPaging.firstTab + 1 : tabPaging.firstTab - 1,
    );
  };

  return (
    <>
      <ul className="flex flex-col">
        {tabHeader ? (
          <div className="text-subhead-s text-headline mb-10 font-bold">
            {tabHeader}
          </div>
        ) : null}

        {tabs?.map((tab, index) => (
          <Disclosure
            as={Fragment}
            key={`${tab.label}-${index}`}
            defaultOpen={index === 0}
          >
            {({ open, close }) => {
              if (!tabRefs.current[index]) {
                tabRefs.current[index] = { button: null, close: () => {} };
              }

              tabRefs.current[index].close = close;
              return (
                <li
                  className={cn(
                    tabPaging.isTabVisible(index) ? "flex" : "hidden",
                    "flex-col",
                    VARIANT_MAPPING[variant].borderColor,
                    !open && "border-b",
                  )}
                >
                  <DisclosureButton
                    ref={(el) => {
                      tabRefs.current[index].button = el;
                    }}
                    className={cn(
                      VARIANT_MAPPING[variant].borderColor,
                      VARIANT_MAPPING[variant].tabButtonGap,
                      "group text-tab-link active:text-button-tertiary-content-pressed active:[&_svg]:fill-button-tertiary-content-pressed flex w-full items-center justify-between py-4 pr-4 text-left",
                    )}
                    data-linktype={tab.dlt}
                    data-linkdetail={tab.dld}
                    data-automation-id={open ? "tab-panel-selected" : "tab"}
                    onClick={() => toggleSelectedPanel(index)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                      if (e.key === "Enter" || e.key === " ")
                        toggleSelectedPanel(index);
                      e.currentTarget.focus();
                    }}
                  >
                    <div className="flex w-full flex-col">
                      <span className={cn(VARIANT_MAPPING[variant].label)}>
                        {tab.label}
                      </span>
                      {variant === "multiline" && (
                        <>
                          {tab.head && (
                            <span className="sm:text-link-m text-link-s">
                              {tab.head}
                            </span>
                          )}
                          {tab.description && (
                            <span className="text-link-s text-body-secondary group-active:text-button-tertiary-content-pressed">
                              {tab.description}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <CaretDownLine
                      fill=""
                      className={cn(
                        open && "rotate-180",
                        "fill-button-tertiary-content -mr-0.5 shrink-0",
                      )}
                    />
                  </DisclosureButton>
                  <DisclosurePanel
                    className={cn(
                      VARIANT_MAPPING[variant].borderColor,
                      "bg-background-highlight border",
                    )}
                    unmount={false}
                  >
                    {children?.[index]}
                    {nextItem?.displayNextItemLink &&
                      index < tabs.length - 1 && (
                        <div className="bg-background-highlight flex flex-row items-center justify-center gap-2 px-6 pb-6">
                          {nextItem?.nextItemText && (
                            <div className="text-body-xs text-body-secondary">
                              {nextItem.nextItemText}
                            </div>
                          )}
                          <Button
                            variant="tertiary"
                            icon="none"
                            theme={theme}
                            className="text-link-inline-content hover:text-link active:text-link-pressed underline decoration-1 underline-offset-auto hover:decoration-2 hover:underline-offset-1"
                            size="m"
                            onClick={() => handleNextTabClick(index)}
                            data-automation-group="next-feature"
                            onKeyDown={(
                              e: React.KeyboardEvent<HTMLButtonElement>,
                            ) => {
                              if (e.key === "Enter" || e.key === " ")
                                handleNextTabClick(index);
                            }}
                            data-linktype={nextItem?.dlt}
                            data-linkdetail={nextItem?.dld}
                          >
                            <span>
                              {index + 1 < tabs.length
                                ? tabs[index + 1]?.label
                                : tabs[0]?.label}
                            </span>
                          </Button>
                        </div>
                      )}
                  </DisclosurePanel>
                </li>
              );
            }}
          </Disclosure>
        ))}
      </ul>

      {showPagination && (
        <div className="mt-6 flex flex-col md:mt-8" style={pagerContainerStyle}>
          <VerticalTabPager
            title={pagerLabel}
            previousArrow={{
              dld: previousAnalytics?.dld,
              dlt: previousAnalytics?.dlt,
              disabled: isFirstDisplayed,
              clickHandler: () => handleArrowsClick(false),
            }}
            nextArrow={{
              dld: nextAnalytics?.dld,
              dlt: nextAnalytics?.dlt,
              disabled: isLastDisplayed,
              clickHandler: () => handleArrowsClick(true),
            }}
          />
        </div>
      )}
    </>
  );
};
